import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import type { StorageObject, WorkspaceType, WorkspaceTypeDefinition } from "@agentry/api-client";
import { useApi } from "../../app/ApiProvider";
import { useAuth, useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	Field,
	LinkButton,
	PageHeader,
	TextInput,
	cx,
} from "../../ui/primitives";
import { EmptyState, ErrorState, LoadingState, recoveryFor } from "../../ui/states";
import { officeThemeFor, useTheme } from "../../shell/theme";
import { formatDateTime } from "./ProgressPages";

/**
 * ワークスペース一覧。選択すると /office 相当の Visual を開く。
 * タイプ／編成の正本は Zerovia Vertical（listWorkspaceTypes）。Agentry は選択 UI のみ。
 */
export function WorkspacePage() {
	const { client, mode } = useApi();
	const { session, scope, setProject } = useAuth();
	const navigate = useNavigate();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [workspaceType, setWorkspaceType] = useState<WorkspaceType | "">("");
	const [reloadSeq, setReloadSeq] = useState(0);
	const tenantId = session?.tenant.id;

	const types = useAsync(async () => {
		if (!tenantId) return { items: [] as WorkspaceTypeDefinition[] };
		return client.listWorkspaceTypes(tenantId);
	}, [client, tenantId, session?.tenant.id]);

	const data = useAsync(async () => {
		if (!tenantId)
			return {
				projects: [] as Awaited<ReturnType<typeof client.listOrganizationProjects>>,
				sessions: [] as Awaited<ReturnType<typeof client.listSessions>>["items"],
			};
		const projects = await client.listOrganizationProjects(tenantId, { includeArchived: true });
		if (!scope)
			return {
				projects,
				sessions: [] as Awaited<ReturnType<typeof client.listSessions>>["items"],
			};
		const sessions = await client.listSessions(scope, { limit: 200 });
		return { projects, sessions: sessions.items };
	}, [client, tenantId, scope?.projectId, reloadSeq, session?.tenant.id]);

	const archive = useAction(async (projectId: string) => {
		if (!tenantId) throw new Error("ログインが必要です");
		return client.archiveProject(tenantId, projectId);
	});

	const restore = useAction(async (projectId: string) => {
		if (!tenantId) throw new Error("ログインが必要です");
		return client.restoreProject(tenantId, projectId);
	});

	const selectedType =
		types.status === "ready"
			? types.data.items.find((item) => item.type === workspaceType)
			: undefined;

	const create = useAction(async () => {
		if (!tenantId) throw new Error("ログインが必要です");
		if (!workspaceType) throw new Error("ワークスペースタイプを選んでください");
		const project = await client.createProject(tenantId, {
			name: name.trim(),
			description: description.trim(),
			workspaceType,
		});
		try {
			await client.ensureProjectGitRepository(tenantId, project.id, {});
		} catch (error) {
			// Project は成功のまま。ensure 失敗は Visual 側で明示＋再試行。
			console.warn("[workspace] ensureProjectGitRepository failed", error);
		}
		return project;
	});

	return (
		<>
			<PageHeader
				eyebrow="ワークスペース"
				title="ワークスペース一覧"
				description="タイプを選んで作成すると、マネージャーハブ編成の Visual を開けます。編成の正本は Zerovia Vertical です。"
			/>

			<Card title="新しいワークスペース" className="mt-4">
				{types.status === "loading" && (
					<p className="text-[13px] text-ink-500">ワークスペースタイプを読み込み中…</p>
				)}
				{types.status === "error" && (
					<div
						role="alert"
						className="rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
					>
						<p className="text-[13px] font-bold text-danger-600">
							{recoveryFor(types.error).title}
						</p>
						<p className="text-[12.5px] text-ink-700">{recoveryFor(types.error).hint}</p>
						<p className="mt-1 text-[12px] text-ink-600">
							Vertical / workspace-types API が未整備、または未接続の可能性があります。黙って
							Mock 成功にはしません。
						</p>
						<button
							type="button"
							className="mt-2 text-[12.5px] font-semibold text-brand-700 underline"
							onClick={types.reload}
						>
							再試行
						</button>
					</div>
				)}
				{types.status === "ready" && types.data.items.length === 0 && (
					<div
						role="alert"
						className="rounded-lg border border-warn-500/40 bg-warn-100 px-3 py-2 text-[13px] text-ink-800"
					>
						利用可能なワークスペースタイプがありません（Vertical 未シード）。
					</div>
				)}

				{types.status === "ready" && types.data.items.length > 0 && (
					<form
						className="grid gap-4"
						onSubmit={async (event) => {
							event.preventDefault();
							const created = await create.run();
							if (!created) return;
							setProject(created.id, created.name);
							setName("");
							setDescription("");
							setWorkspaceType("");
							setReloadSeq((value) => value + 1);
							navigate(`/app/workspace/${created.id}${mode === "mock" ? "?api=mock" : ""}`);
						}}
					>
						<Field label="ワークスペースタイプ">
							<div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
								{types.data.items.map((item) => {
									const selected = workspaceType === item.type;
									return (
										<button
											key={item.type}
											type="button"
											aria-label={item.displayName}
											aria-pressed={selected}
											onClick={() => setWorkspaceType(item.type)}
											className={cx(
												"rounded-xl border px-3 py-3 text-left transition",
												selected
													? "border-brand-400 bg-brand-50 ring-1 ring-brand-200"
													: "border-ink-200 bg-white hover:border-brand-300",
											)}
										>
											<p className="text-[14px] font-bold text-ink-900">{item.displayName}</p>
											<p className="mt-1 line-clamp-2 text-[12px] text-ink-500">
												{item.description}
											</p>
										</button>
									);
								})}
							</div>
						</Field>

						{selectedType ? (
							<div className="rounded-xl border border-ink-200 bg-ink-50/50 px-4 py-3">
								<p className="text-[13px] font-bold text-ink-900">編成（Vertical より）</p>
								<p className="mt-1 text-[12.5px] text-ink-600">
									Vertical: <code className="text-[12px]">{selectedType.verticalKey}</code>
								</p>
								<ul className="mt-2 flex flex-wrap gap-2">
									{selectedType.roles.map((role) => (
										<li key={role.key}>
											<Badge tone={role.kind === "human" ? "brand" : "neutral"}>
												{role.displayName}
												{role.kind === "agent" ? "（Agent）" : ""}
											</Badge>
										</li>
									))}
								</ul>
								<p className="mt-3 text-[12.5px] leading-relaxed text-ink-600">
									実行ループ: Visual 上でマネージャー／担当へ指示 → Agent が作業・報告 →
									マネージャーが確認または納品。進捗はワークスペース内の Visual と会話履歴で確認します。
								</p>
							</div>
						) : (
							<p className="text-[12.5px] text-ink-500">タイプを選ぶと編成が表示されます。</p>
						)}

						<div className="grid gap-3 md:grid-cols-[1fr_2fr_auto] md:items-end">
							<Field label="ワークスペース名">
								<TextInput
									value={name}
									required
									maxLength={120}
									placeholder="例: 春のキャンペーン"
									onChange={(event) => setName(event.target.value)}
								/>
							</Field>
							<Field label="説明（任意）">
								<TextInput
									value={description}
									maxLength={2000}
									placeholder="用途や担当のメモ"
									onChange={(event) => setDescription(event.target.value)}
								/>
							</Field>
							<Button
								type="submit"
								disabled={
									create.pending || !name.trim() || !workspaceType || types.status !== "ready"
								}
							>
								{create.pending ? "作成中…" : "作成して開く"}
							</Button>
						</div>
					</form>
				)}

				<div className="mt-4">
					<Callout tone="neutral" title="成果物の置き場">
						コードは作成後に Gitea（Project ごと private）へ ensure します。大きいファイルは
						Zerovia Artifact（メタは DB、実体は object-store）境界です。両導線を混同しません。
					</Callout>
				</div>

				{create.error ? (
					<div
						role="alert"
						className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
					>
						<p className="text-[13px] font-bold text-danger-600">
							{recoveryFor(create.error).title}
						</p>
						<p className="text-[12.5px] text-ink-700">{recoveryFor(create.error).hint}</p>
					</div>
				) : null}
			</Card>

			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(() => {
					const active = data.data.projects.filter((project) => project.status !== "ARCHIVED");
					const archived = data.data.projects.filter((project) => project.status === "ARCHIVED");
					if (active.length === 0 && archived.length === 0) {
						return (
							<EmptyState
								title="ワークスペースがありません"
								description="上のフォームから作成できます。"
							/>
						);
					}
					return (
						<>
							{active.length === 0 ? (
								<EmptyState
									title="表示中のワークスペースがありません"
									description="上のフォームから作成するか、下の削除済みから復元できます。"
								/>
							) : (
								<div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
									{active.map((project) => {
										const related = data.data.sessions.filter(
											(session) => session.projectId === project.id,
										);
										const activeSessions = related.filter(
											(session) => session.status === "ACTIVE",
										).length;
										const selected = project.id === scope?.projectId;
										const typeLabel =
											types.status === "ready"
												? types.data.items.find((item) => item.type === project.workspaceType)
														?.displayName
												: project.workspaceType;
										return (
											<div
												key={project.id}
												className={cx(
													"rounded-2xl border px-4 py-4 text-left shadow-sm transition",
													selected
														? "border-brand-300 bg-brand-50/60 ring-1 ring-brand-200"
														: "border-ink-200 bg-white hover:border-brand-300 hover:bg-brand-50/30",
												)}
											>
												<button
													type="button"
													className="flex w-full items-start gap-3 text-left"
													onClick={() => {
														setProject(project.id, project.name);
														navigate(
															`/app/workspace/${project.id}${mode === "mock" ? "?api=mock" : ""}`,
														);
													}}
												>
													<span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
														<OfficeGlyph />
													</span>
													<div className="min-w-0 flex-1">
														<p className="truncate text-[15px] font-bold text-ink-900">
															{project.name}
														</p>
														<p className="mt-0.5 line-clamp-2 text-[12.5px] text-ink-500">
															{project.description ||
																`${session?.user.name ?? "ユーザー"} の作業机`}
														</p>
														<div className="mt-3 flex flex-wrap items-center gap-2">
															<Badge tone={selected ? "brand" : "neutral"}>
																{selected ? "選択中" : "開く"}
															</Badge>
															{typeLabel ? <Badge tone="neutral">{typeLabel}</Badge> : null}
															<span className="text-[12px] text-ink-500">
																依頼 {related.length} · 進行中 {activeSessions}
															</span>
														</div>
													</div>
												</button>
												<div className="mt-3 flex justify-end border-t border-ink-100 pt-3">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														disabled={archive.pending}
														onClick={async (event) => {
															event.stopPropagation();
															const ok = window.confirm(
																`「${project.name}」を削除しますか？\nデータ（会話・成果物・コード）は残し、一覧から非表示にします。あとから復元できます。`,
															);
															if (!ok) return;
															const result = await archive.run(project.id);
															if (!result) return;
															if (scope?.projectId === project.id) {
																const next = active.find((item) => item.id !== project.id);
																if (next) setProject(next.id, next.name);
															}
															setReloadSeq((value) => value + 1);
														}}
													>
														{archive.pending ? "削除中…" : "削除"}
													</Button>
												</div>
											</div>
										);
									})}
								</div>
							)}
							{archive.error ? (
								<div
									role="alert"
									className="mt-3 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
								>
									<p className="text-[13px] font-bold text-danger-600">
										{recoveryFor(archive.error).title}
									</p>
									<p className="text-[12.5px] text-ink-700">{recoveryFor(archive.error).hint}</p>
								</div>
							) : null}
							{archived.length > 0 ? (
								<div className="mt-6">
									<p className="text-[13px] font-bold text-ink-700">削除済み（データ保持）</p>
									<ul className="mt-2 space-y-2">
										{archived.map((project) => (
											<li
												key={project.id}
												className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ink-200 bg-ink-50/60 px-3 py-2"
											>
												<div className="min-w-0">
													<p className="truncate text-[14px] font-semibold text-ink-800">
														{project.name}
													</p>
													<p className="text-[12px] text-ink-500">論理削除済み · データは残っています</p>
												</div>
												<Button
													type="button"
													variant="secondary"
													size="sm"
													disabled={restore.pending}
													onClick={async () => {
														const result = await restore.run(project.id);
														if (result) setReloadSeq((value) => value + 1);
													}}
												>
													{restore.pending ? "復元中…" : "復元"}
												</Button>
											</li>
										))}
									</ul>
									{restore.error ? (
										<div
											role="alert"
											className="mt-2 rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
										>
											<p className="text-[13px] font-bold text-danger-600">
												{recoveryFor(restore.error).title}
											</p>
											<p className="text-[12.5px] text-ink-700">
												{recoveryFor(restore.error).hint}
											</p>
										</div>
									) : null}
								</div>
							) : null}
						</>
					);
				})()}
		</>
	);
}

/**
 * 選択したワークスペースの Office Visual（/office を埋め込み）。
 */
export function WorkspaceVisualPage() {
	const { client, mode } = useApi();
	const { setProject } = useAuth();
	const { themeId } = useTheme();
	const scope = useScope();
	const { projectId = "" } = useParams();
	const navigate = useNavigate();

	const data = useAsync(
		() => client.listOrganizationProjects(scope.tenantId),
		[client, scope.tenantId],
	);

	const project =
		data.status === "ready"
			? data.data.find((item) => item.id === projectId && item.status !== "ARCHIVED")
			: undefined;

	useEffect(() => {
		if (project) setProject(project.id, project.name);
	}, [project, setProject]);

	const officeSrc = useMemo(() => {
		const params = new URLSearchParams();
		params.set("embed", "1");
		params.set("project", projectId);
		params.set("viz", officeThemeFor(themeId));
		if (mode === "mock") params.set("api", "mock");
		return `/office/?${params.toString()}`;
	}, [mode, projectId, themeId]);

	useEffect(() => {
		const frame = document.querySelector<HTMLIFrameElement>("iframe.ag-workspace-visual-frame");
		if (!frame?.contentWindow) return;
		frame.contentWindow.postMessage(
			{ type: "agentry:viz-theme", viz: officeThemeFor(themeId) },
			window.location.origin,
		);
	}, [themeId]);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;
	if (!project) {
		return (
			<EmptyState
				title="ワークスペースが見つかりません"
				description="一覧から選び直してください。"
				action={
					<button
						type="button"
						className="rounded-lg border border-ink-300 bg-white px-4 py-2 text-[14px] font-semibold"
						onClick={() => navigate("/app/workspace")}
					>
						一覧へ戻る
					</button>
				}
			/>
		);
	}

	return (
		<div className="ag-workspace-visual flex min-h-0 flex-1 flex-col">
			<WorkspacePathsPanel
				organizationId={scope.tenantId}
				projectId={project.id}
				projectName={project.name}
				className="shrink-0 border-b border-ink-200 bg-white px-3 py-2"
			/>
			<p className="shrink-0 border-b border-ink-100 bg-ink-50/80 px-3 py-2 text-[12.5px] text-ink-700">
				依頼は Visual 内の<strong>マネージャー席</strong>→「依頼する」。整理中は席の画面が明るくなり、上部の
				<strong>依頼の状態</strong>に経過が出ます（再依頼は完了まで不可。会話履歴には残りません）。
			</p>
			<iframe
				title={`${project.name} の Office Visual`}
				src={officeSrc}
				className="ag-workspace-visual-frame min-h-0 flex-1"
			/>
		</div>
	);
}

function WorkspacePathsPanel({
	organizationId,
	projectId,
	projectName,
	className,
}: {
	organizationId: string;
	projectId: string;
	projectName: string;
	className?: string;
}) {
	const { client, mode } = useApi();
	const { session } = useAuth();
	const [reloadSeq, setReloadSeq] = useState(0);
	const repos = useAsync(
		() => client.listProjectGitRepositories(organizationId, projectId),
		[client, organizationId, projectId, reloadSeq],
	);
	const ensure = useAction(async () =>
		client.ensureProjectGitRepository(organizationId, projectId, {}),
	);

	const primary = repos.status === "ready" ? repos.data[0] : undefined;
	const userFolder = session?.user.name ?? "ユーザー";
	/** ストレージ画面と同じ階層: ユーザーフォルダ / ワークスペースフォルダ */
	const storagePath = `${userFolder} / ${projectName}`;
	const storageHref = `/app/storage?ws=${encodeURIComponent(projectId)}${mode === "mock" ? "&api=mock" : ""}`;

	return (
		<div className={cx(className, "space-y-2")}>
			{repos.status === "loading" && (
				<p className="text-[12.5px] text-ink-500">コードリポジトリを確認中…</p>
			)}
			{repos.status === "error" && (
				<div className="flex flex-wrap items-center gap-2" role="alert">
					<p className="text-[12.5px] font-semibold text-danger-600">
						{recoveryFor(repos.error).title}
					</p>
					<p className="text-[12px] text-ink-600">{recoveryFor(repos.error).hint}</p>
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={ensure.pending}
						onClick={() => {
							void (async () => {
								await ensure.run();
								setReloadSeq((value) => value + 1);
							})();
						}}
					>
						再試行
					</Button>
				</div>
			)}
			{repos.status === "ready" && primary && (
				<div className="flex flex-wrap items-center gap-2 text-[12.5px]">
					<Badge tone="success">コード（Gitea）</Badge>
					<span className="font-semibold text-ink-900">{primary.name}</span>
					<code className="truncate text-[11.5px] text-ink-600">{primary.cloneUrl}</code>
					<a
						href={primary.cloneUrl}
						className="font-semibold text-brand-700 underline"
						target="_blank"
						rel="noreferrer"
					>
						clone URL
					</a>
				</div>
			)}
			{repos.status === "ready" && !primary && (
				<div className="flex flex-wrap items-center gap-2" role="status">
					<p className="text-[12.5px] text-ink-700">
						コード用 Gitea リポジトリはまだありません（ensure 未実行または失敗）。
					</p>
					{ensure.error ? (
						<p className="text-[12px] font-semibold text-danger-600">
							{recoveryFor(ensure.error).title}: {recoveryFor(ensure.error).hint}
						</p>
					) : null}
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={ensure.pending}
						onClick={async () => {
							const result = await ensure.run();
							if (result) setReloadSeq((value) => value + 1);
						}}
					>
						{ensure.pending ? "準備中…" : "リポジトリを準備（再試行）"}
					</Button>
				</div>
			)}

			<div className="flex flex-wrap items-center gap-2 text-[12.5px]">
				<Badge tone="neutral">ストレージ</Badge>
				<span className="font-semibold text-ink-900">成果物の格納パス</span>
				<code className="truncate text-[11.5px] text-ink-600" title={storagePath}>
					{storagePath}
				</code>
				<Link className="font-semibold text-brand-700 underline" to={storageHref}>
					フォルダを開く
				</Link>
			</div>
		</div>
	);
}

/**
 * ストレージ: ログインユーザーのユーザーフォルダ配下に、ワークスペースごとのフォルダを置く。
 */
export function StoragePage() {
	const { client } = useApi();
	const { session } = useAuth();
	const scope = useScope();
	const [params, setParams] = useSearchParams();
	const openWorkspaceId = params.get("ws");

	const data = useAsync(async () => {
		const [projects, objects, artifacts] = await Promise.all([
			client.listOrganizationProjects(scope.tenantId),
			client.listStorageObjects(scope),
			client.listArtifacts(scope, { limit: 200 }),
		]);
		return { projects, objects, artifacts: artifacts.items };
	}, [client, scope.tenantId, scope.projectId]);

	const userFolder = session?.user.name ?? "ユーザー";

	const workspaceFolders = useMemo(() => {
		if (data.status !== "ready") return [];
		return data.data.projects.map((project) => {
			const files = data.data.objects.filter((object) => object.projectId === project.id);
			return { project, files };
		});
	}, [data]);

	const openFolder = workspaceFolders.find((folder) => folder.project.id === openWorkspaceId);

	return (
		<>
			<PageHeader
				eyebrow="ストレージ"
				title={`${userFolder} のフォルダ`}
				description="ログイン中のユーザー専用フォルダです。中はワークスペースごとに分かれています。大きい成果物は Artifact、コードは Gitea（…/git/repos）を使います。"
				actions={
					data.status === "ready" && (
						<button
							type="button"
							onClick={data.reload}
							className="rounded-lg border border-ink-300 bg-white px-4 py-2 text-[14px] font-semibold text-ink-800 hover:bg-ink-50"
						>
							再取得する
						</button>
					)
				}
			/>

			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" && (
				<div className="mt-4 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-shell">
					<nav
						aria-label="ストレージの場所"
						className="flex flex-wrap items-center gap-1.5 border-b border-ink-100 bg-ink-50/80 px-4 py-3 text-[13px]"
					>
						<button
							type="button"
							className={cx(
								"rounded-lg px-2 py-1 font-semibold",
								openFolder ? "text-brand-700 hover:bg-white" : "bg-white text-ink-900 shadow-sm",
							)}
							onClick={() => {
								const next = new URLSearchParams(params);
								next.delete("ws");
								setParams(next, { replace: true });
							}}
						>
							👤 {userFolder}
						</button>
						{openFolder && (
							<>
								<span className="text-ink-300" aria-hidden="true">
									/
								</span>
								<span className="rounded-lg bg-white px-2 py-1 font-semibold text-ink-900 shadow-sm">
									📁 {openFolder.project.name}
								</span>
							</>
						)}
					</nav>

					<div className="p-4 sm:p-5">
						{!openFolder ? (
							workspaceFolders.length === 0 ? (
								<EmptyState title="ワークスペースフォルダがありません" />
							) : (
								<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
									{workspaceFolders.map(({ project, files }) => (
										<button
											key={project.id}
											type="button"
											onClick={() => {
												const next = new URLSearchParams(params);
												next.set("ws", project.id);
												setParams(next, { replace: true });
											}}
											className="flex items-start gap-3 rounded-2xl border border-ink-200 bg-ink-50/40 px-4 py-4 text-left transition hover:border-brand-300 hover:bg-brand-50/40"
										>
											<span className="text-[28px]" aria-hidden="true">
												📁
											</span>
											<div className="min-w-0">
												<p className="truncate text-[14px] font-bold text-ink-900">
													{project.name}
												</p>
												<p className="mt-0.5 text-[12px] text-ink-500">
													{files.length} 件のファイル
												</p>
											</div>
										</button>
									))}
								</div>
							)
						) : openFolder.files.length === 0 ? (
							<EmptyState title="このワークスペースに保存ファイルはありません" />
						) : (
							<ul className="divide-y divide-ink-100 rounded-xl border border-ink-100">
								{openFolder.files.map((object) => (
									<StorageFileRow
										key={object.id}
										object={object}
										artifactName={
											data.data.artifacts.find((artifact) => artifact.id === object.artifactId)
												?.name
										}
									/>
								))}
							</ul>
						)}
					</div>
				</div>
			)}
		</>
	);
}

function StorageFileRow({
	object,
	artifactName,
}: {
	object: StorageObject;
	artifactName?: string;
}) {
	const expired = object.retentionUntil ? Date.parse(object.retentionUntil) < Date.now() : false;
	return (
		<li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
			<div className="flex min-w-0 items-start gap-3">
				<span className="text-[20px]" aria-hidden="true">
					📄
				</span>
				<div className="min-w-0">
					{artifactName ? (
						<Link
							to={`/app/artifacts/${object.artifactId}`}
							className="block truncate text-[14px] font-semibold text-ink-900 hover:text-brand-700"
						>
							{artifactName}
						</Link>
					) : (
						<p className="truncate font-mono text-[12px] text-ink-500">{object.artifactId}</p>
					)}
					<p className="mt-0.5 text-[12px] text-ink-500">
						{object.mimeType} · {object.size.toLocaleString()} B ·{" "}
						<span className={expired ? "text-danger-600" : undefined}>
							{object.retentionUntil
								? `保管期限 ${formatDateTime(object.retentionUntil)}`
								: "期限なし"}
						</span>
					</p>
				</div>
			</div>
			<div className="flex items-center gap-2">
				<Badge tone={object.storageClass === "ARCHIVE" ? "warning" : "neutral"}>
					{object.storageClass === "ARCHIVE" ? "長期保管" : "標準"}
				</Badge>
				{artifactName && (
					<LinkButton to={`/app/artifacts/${object.artifactId}`} variant="secondary" size="sm">
						開く
					</LinkButton>
				)}
			</div>
		</li>
	);
}

export function ProjectListPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(async () => {
		const [projects, sessions] = await Promise.all([
			client.listProjects(scope),
			client.listSessions(scope, { limit: 200 }),
		]);
		return { projects, sessions };
	}, [client, scope.tenantId, scope.projectId]);

	return (
		<>
			<PageHeader
				eyebrow="プロジェクト"
				title="案件ごとの依頼と成果物"
				description="同じ案件の依頼をまとめて確認できます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.projects.length === 0 ? (
					<EmptyState title="プロジェクトがありません" />
				) : (
					<div className="grid gap-4 lg:grid-cols-2">
						{data.data.projects.map((project) => {
							const related = data.data.sessions.items.filter((s) => s.projectId === project.id);
							return (
								<Card
									key={project.id}
									title={project.name}
									description={project.description}
									action={
										<LinkButton to={`/app/projects/${project.id}`} variant="secondary" size="sm">
											詳細
										</LinkButton>
									}
								>
									<p className="text-[13px] text-ink-600">{related.length} 件の依頼</p>
								</Card>
							);
						})}
					</div>
				))}
		</>
	);
}

export function ProjectDetailPage() {
	const { client } = useApi();
	const scope = useScope();
	const { projectId = scope.projectId } = useParams();

	const data = useAsync(async () => {
		const [projects, sessions, artifacts] = await Promise.all([
			client.listProjects(scope),
			client.listSessions(scope, { limit: 200 }),
			client.listArtifacts(scope, { limit: 200 }),
		]);
		return { projects, sessions, artifacts };
	}, [client, scope.tenantId, scope.projectId, projectId]);

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") return <ErrorState error={data.error} onRetry={data.reload} />;

	const project = data.data.projects.find((p) => p.id === projectId);
	if (!project) {
		return <EmptyState title="プロジェクトが見つかりません" description="URLをご確認ください。" />;
	}
	const sessions = data.data.sessions.items.filter((s) => s.projectId === projectId);
	const artifacts = data.data.artifacts.items.filter((a) => a.projectId === projectId);

	return (
		<>
			<PageHeader eyebrow="プロジェクト" title={project.name} description={project.description} />
			<div className="grid gap-4 lg:grid-cols-2">
				<Card title="この案件の依頼">
					{sessions.length === 0 ? (
						<EmptyState title="依頼はまだありません" />
					) : (
						<ul className="divide-y divide-ink-100">
							{sessions.map((item) => (
								<li key={item.id} className="py-2 first:pt-0 last:pb-0">
									<Link
										to={`/app/sessions/${item.id}`}
										className="text-[14px] font-semibold text-ink-900 hover:text-brand-600"
									>
										{item.title}
									</Link>
								</li>
							))}
						</ul>
					)}
				</Card>
				<Card title="この案件の成果物">
					{artifacts.length === 0 ? (
						<EmptyState title="成果物はまだありません" />
					) : (
						<ul className="divide-y divide-ink-100">
							{artifacts.map((artifact) => (
								<li
									key={artifact.id}
									className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
								>
									<Link
										to={`/app/artifacts/${artifact.id}`}
										className="truncate text-[14px] text-ink-900 hover:text-brand-600"
									>
										{artifact.name}
									</Link>
									<Badge tone="neutral">v{artifact.version}</Badge>
								</li>
							))}
						</ul>
					)}
				</Card>
			</div>
		</>
	);
}

function OfficeGlyph() {
	return (
		<svg viewBox="0 0 24 24" className="size-5" fill="none" aria-hidden="true">
			<path
				d="M5 6.5A1.5 1.5 0 0 1 6.5 5h11A1.5 1.5 0 0 1 19 6.5v11a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 17.5v-11Z"
				stroke="currentColor"
				strokeWidth="1.8"
			/>
			<path d="M5 9.5h14M10 9.5V19" stroke="currentColor" strokeWidth="1.8" />
		</svg>
	);
}
